import api from 'api';
import { BoxWrapper, Button } from 'components/Base';
import React, { useEffect, useState } from 'react';
import { MetaType } from 'store/modules/Meta/types';
import { Notify } from 'utils';
import { CircularProgress, makeStyles } from '@material-ui/core';
import BenchmarkTable from 'components/Benchmarks/BenchmarkTable';

type PpBenchmarkTogglesProps = { isFirmAdmin: boolean };

const useStyles = makeStyles({
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10rem'
  }
});

const PpBenchmarkToggles: React.FC<PpBenchmarkTogglesProps> = ({
  isFirmAdmin
}) => {
  const classes = useStyles();
  const [fintegrateBenchmarks, setFintegrateBenchmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [benchmarksFirmConfig, setBenchmarksFirmConfig] = useState(
    [] as number[]
  );
  const [morningstarBenchmarks, setMorningstarBenchmarks] = useState([]);
  const [morningstarBenchmarkConfig, setMorningstarBenchmarkConfig] = useState(
    []
  );
  const [benchmarks, setBenchmarks] = useState([] as any);

  const saveBenchMarks = () => {
    api
      .updateBenchmarksConfig(
        benchmarks.filter(
          (benchmark: any) =>
            (benchmark.benchmarkType === 'firm' && benchmark.selected) ||
            (benchmark.benchmarkType === 'morningstar' && !benchmark.selected)
        )
      )
      .then(response => {
        if (response.status === 200)
          Notify.success('Your benchmarks were sucessfully updated!');
      })
      .catch(err => Notify.error(err));
  };

  useEffect(() => {
    Promise.all([
      api.getBenchmarks(),
      api.getBenchmarkConfig(),
      api.getMorningstarBenchmarks(),
      api.getMorningstarBenchmarkConfig()
    ])
      .then(
        ([
          benchmarks,
          benchmarkConfig,
          morningstarBenchmarkResponse,
          morningstarBenchmarkConfig
        ]) => {
          setFintegrateBenchmarks(benchmarks.data.sort());
          setBenchmarksFirmConfig(
            benchmarkConfig.data.sort(
              (a: MetaType, b: MetaType) => Number(a.value) - Number(b.value)
            )
          );
          setMorningstarBenchmarks(morningstarBenchmarkResponse.data);
          setMorningstarBenchmarkConfig(morningstarBenchmarkConfig.data);
        }
      )
      .catch(() => Notify.error('Benchmarks could not be fetched.'))
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const excludedMorningstarBenchmarks = morningstarBenchmarkConfig.map(
      (result: any) => result.external_id
    );
    setBenchmarks([
      ...fintegrateBenchmarks.map((benchmark: any) => {
        return {
          ...benchmark,
          type: benchmark.name.split(' ')[0],
          selected: benchmarksFirmConfig.includes(parseInt(benchmark.value)),
          benchmarkType: 'firm'
        };
      }),
      ...Object.values(morningstarBenchmarks).flatMap((group: any) => {
        return group.benchmarks.map((benchmark: any) => {
          return {
            name: benchmark.name,
            value: benchmark.secId,
            type: group.name,
            selected: !excludedMorningstarBenchmarks.includes(benchmark.secId),
            benchmarkType: 'morningstar'
          };
        });
      })
    ]);
  }, [
    fintegrateBenchmarks,
    morningstarBenchmarks,
    benchmarksFirmConfig,
    morningstarBenchmarkConfig
  ]);

  return (
    <div>
      <BoxWrapper
        title="Past Performance Benchmark Toggles"
        subTitle={
          <div>
            Choose from a variety of industry benchmarks, including IA Sectors,
            ABI Sectors, Morningstar Categories, ARC, Cash, Inflation, and
            Property. By default, all benchmarks are pre-selected to enable
            firm-wide comparison against investment strategies.
            <div>
              To restrict access, simply deselect benchmarks you wish to exclude
              from the available options.
            </div>
          </div>
        }
      >
        {isLoading ? (
          <div className={classes.loaderWrapper}>
            <CircularProgress size="80px" />
          </div>
        ) : (
          <div
            style={{
              height: 'max-content',
              margin: '8px',
              padding: '8px'
            }}
          >
            <BenchmarkTable
              benchmarks={benchmarks}
              setBenchmarks={setBenchmarks}
              canSelectAll
              lockedToFirmAdmins
            />
            {isFirmAdmin && (
              <div style={{ marginTop: '16px' }}>
                <Button onClick={saveBenchMarks}>Remove Benchmarks</Button>
              </div>
            )}
          </div>
        )}
      </BoxWrapper>
    </div>
  );
};

export default PpBenchmarkToggles;
